import type { NavDomain } from './types';

/** Personal rail placement and names. Destination IDs always resolve from the registry. */
export interface SidebarLayout {
  sections: Record<string, string[]>;
  labels: Record<string, string>;
}

export const EMPTY_SIDEBAR_LAYOUT: SidebarLayout = { sections: {}, labels: {} };
export const MAX_SIDEBAR_LABEL_LENGTH = 80;
const RETIRED_DESTINATION_IDS = new Set(['owner-dashboard']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isControlCharacter(character: string): boolean {
  const code = character.charCodeAt(0);
  return code < 32 || code === 127;
}

export function isSidebarPreferenceKey(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= 256 &&
    !Array.from(value).some(isControlCharacter) &&
    !['__proto__', 'prototype', 'constructor'].includes(value);
}

/** Removed product pages must not survive as a saved pin, placement, or name. */
export function isCurrentSidebarDestinationKey(value: unknown): value is string {
  return isSidebarPreferenceKey(value) && !RETIRED_DESTINATION_IDS.has(value);
}

export function normalizeSidebarLabel(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const label = Array.from(value).map((character) => isControlCharacter(character) ? ' ' : character)
    .join('').replace(/\s+/g, ' ').trim();
  return label ? label.slice(0, MAX_SIDEBAR_LABEL_LENGTH) : undefined;
}

/** localStorage is untrusted; accept only the small data shape the rail understands. */
export function normalizeSidebarLayout(value: unknown): SidebarLayout {
  if (!isRecord(value)) return { sections: {}, labels: {} };
  const seen = new Set<string>();
  const sections = isRecord(value.sections)
    ? Object.fromEntries(Object.entries(value.sections).flatMap(([domainId, ids]) => {
        if (!isSidebarPreferenceKey(domainId) || !Array.isArray(ids)) return [];
        const items = ids.filter((id): id is string => {
          if (!isCurrentSidebarDestinationKey(id) || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
        return [[domainId, items]];
      }))
    : {};
  const labels = isRecord(value.labels)
    ? Object.fromEntries(Object.entries(value.labels).flatMap(([id, rawLabel]) => {
        const label = normalizeSidebarLabel(rawLabel);
        return isCurrentSidebarDestinationKey(id) && label ? [[id, label]] : [];
      }))
    : {};
  return { sections, labels };
}

/** Encoding both identities as a tuple prevents delimiter collisions. */
export function sidebarPreferenceScope(tenantId: string, userId: string): string {
  return JSON.stringify([tenantId, userId]);
}

/**
 * Apply only to an already RBAC-filtered tree. Stored IDs never create a page,
 * permission, route, or section. New registry pages retain their default section.
 * Sections emptied by a move remain available as drop targets.
 */
export function applySidebarLayout(groups: readonly NavDomain[], rawLayout: SidebarLayout): NavDomain[] {
  const layout = normalizeSidebarLayout(rawLayout);
  const destinations = new Map(groups.flatMap((group) => group.items.map((item) => [item.id, item] as const)));
  const placed = new Set<string>();
  const result = groups.map((group) => ({ ...group, items: [] as NavDomain['items'] }));

  for (const group of result) {
    for (const id of layout.sections[group.id] ?? []) {
      const item = destinations.get(id);
      if (!item || placed.has(id)) continue;
      group.items.push(layout.labels[id] ? { ...item, label: layout.labels[id] } : item);
      placed.add(id);
    }
  }
  for (const [index, group] of groups.entries()) {
    for (const item of group.items) {
      if (placed.has(item.id)) continue;
      result[index].items.push(layout.labels[item.id] ? { ...item, label: layout.labels[item.id] } : item);
      placed.add(item.id);
    }
  }
  return result;
}
